import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import HelpIcon from '../HelpIcon';
import { formatMoneyExact } from '../../utils/format';
import { creditNotice, sharedReasonArgs, overrideReasonOk, OVERRIDE_REASON_MAX } from './creditLimitDisplay.mjs';
import DecisionDrawer from '../../shell/drawer/DecisionDrawer';
import { newIdempotencyKey } from './creditNoteHelpers.mjs';

import { emoText } from '../../shell/EmojiIcons';
// GES-15 — the inline credit-gate notice for every sale-write surface. Red BLOCK (422, non-admin), amber WARN (409,
// admin) with ONE explicit "Proceed anyway" that re-sends the SAME request with credit_override:true, 503
// unavailable (admin may proceed, D16), 500 audit failure. Figures are the API's exact text, formatted only.
// maxWidth 640 + whiteSpace normal keep the notice readable inside wide nowrap tables (Review & Post row).
const TONE = {
  block: ['var(--st-crit-bg, #fee2e2)', 'var(--st-crit, #b91c1c)', '#f0c0c0'],
  audit_failed: ['var(--st-crit-bg, #fee2e2)', 'var(--st-crit, #b91c1c)', '#f0c0c0'],
  override_refused: ['var(--st-crit-bg, #fee2e2)', 'var(--st-crit, #b91c1c)', '#f0c0c0'],
  warn: ['var(--st-warn-bg, #fef3c7)', 'var(--st-warn, #b45309)', '#fcd34d'],
  unavailable: ['var(--st-warn-bg, #fef3c7)', 'var(--st-warn, #b45309)', '#fcd34d'],
};

function Figures({ model, t }) {
  if (!model.figures.length) return null;
  const val = (k, v) => (k === 'limit' && model.state === 'cash_only' ? t('credit.limitCashOnly') : formatMoneyExact(v));
  const rows = model.figures.map(([k, v]) => [t(`credit.fig.${k}`), val(k, v)]);
  if (model.headroom) rows.push([t(model.headroom.over ? 'credit.fig.overBy' : 'credit.fig.headroom'), formatMoneyExact(model.headroom.amount)]);
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'start', gap: '2px 24px', margin: '6px 0 0', fontSize: 12 }}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}><dt style={{ color: 'inherit', opacity: 0.85 }}>{emoText(label)}</dt>
          <dd style={{ margin: 0, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</dd></React.Fragment>
      ))}
    </dl>
  );
}

// E5 (GES-440): the admin override = a DecisionDrawer asking for the reason (3-500 chars). onConfirm gets the fields to MERGE into the SAME
// request: credit_override, credit_override_reason and a NEW idempotency key (the refused attempt wrote nothing).
function OverrideDrawer({ model, onConfirm, onClose }) {
  const { t } = useTranslation('sales');
  const [reason, setReason] = useState('');
  const kv = [[t('credit.override.customer'), model.partner || '-'], ...model.figures.map(([k, v]) => [t(`credit.fig.${k}`), k === 'limit' && model.state === 'cash_only' ? t('credit.limitCashOnly') : formatMoneyExact(v)])];
  return (
    <DecisionDrawer testId="credit-override-drawer" eyebrow={t('credit.override.eyebrow')} title={t('credit.override.title')} kv={kv} hint={t('credit.override.hint')} onClose={onClose}
      reason={{ value: reason, onChange: setReason, label: t('credit.override.reason'), placeholder: t('credit.override.placeholder'), required: true, max: OVERRIDE_REASON_MAX, testId: 'credit-override-reason', requiredWhy: t('credit.override.reasonWhy') }}
      confirm={{ label: t('credit.override.confirm'), danger: false, testId: 'credit-override-confirm', disabled: !overrideReasonOk(reason), why: t('credit.override.reasonWhy'),
        onClick: () => onConfirm({ credit_override: true, credit_override_reason: reason.trim(), idempotency_key: newIdempotencyKey() }) }} />
  );
}

export default function CreditLimitNotice({ model, onProceed = null, onDismiss = null, busy = false }) {
  const { t, i18n } = useTranslation('sales');
  const { isAdmin } = useAuth();
  const [drawer, setDrawer] = useState(false);
  if (!model) return null;
  const [bg, fg, border] = TONE[model.kind];
  const intro = model.kind === 'block' && model.state === 'cash_only' ? 'cash_only' : model.kind === 'unavailable' && onProceed ? 'unavailableAdmin' : model.kind;
  const text = (i18n.language || '').startsWith('fr') && model.messageFr ? model.messageFr : model.message;
  const canOverride = model.canOverride && isAdmin !== false;   // the server decides (409 vs 422); a non-admin never gets past the greyed button
  return (
    <div role={model.kind === 'block' || model.kind === 'audit_failed' || model.kind === 'override_refused' ? 'alert' : 'status'} data-testid={`credit-notice-${model.kind}`}
      style={{ background: bg, color: fg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.45, maxWidth: 640, boxSizing: 'border-box', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
      <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        {t(`credit.kind.${model.kind}`)}{model.partner ? ` · ${model.partner}` : ''}
        <HelpIcon helpKey={model.kind === 'warn' ? 'sales.credit.warning' : 'sales.credit.blocked'} />
      </div>
      <div style={{ marginTop: 3 }}>{model.kind === 'override_refused' ? text : t(`credit.intro.${intro}`, { partner: model.partner })}</div>
      {model.sharedAccount && <div data-testid="credit-reason-shared" style={{ marginTop: 3, fontWeight: 600 }}>{t(...sharedReasonArgs(model.sharedAccount.customer || model.partner, model.sharedAccount.account_name))}</div>}
      <Figures model={model} t={t} />
      {model.kind === 'audit_failed' && text && <div style={{ marginTop: 4 }}>{text}</div>}
      {(onProceed || onDismiss) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {onProceed && model.showOverride && <button type="button" className="btn" data-testid="credit-proceed" disabled={busy || !canOverride}
            title={canOverride ? undefined : t('credit.override.adminOnly')} onClick={() => setDrawer(true)}>{t('credit.override.button')}</button>}
          {onDismiss && <button type="button" className="btn" disabled={busy} onClick={onDismiss}>{t('credit.dismiss')}</button>}
        </div>
      )}
      {drawer && onProceed && <OverrideDrawer model={model} onClose={() => setDrawer(false)} onConfirm={(ov) => { setDrawer(false); onProceed(ov); }} />}
    </div>
  );
}

// One refusal at a time per surface. catchCredit(body, resend, partnerName?) -> true when it was a credit refusal (the caller
// stops its own error path); resend() must re-send the SAME request with credit_override:true. Nothing is ever
// re-sent automatically: only the Proceed click does it.
export function useCreditGate() {
  const { isAdmin } = useAuth();
  const [pending, setPending] = useState(null);
  const clear = useCallback(() => setPending(null), []);
  const catchCredit = useCallback((body, resend, partnerName = '') => {
    const model = creditNotice(body, isAdmin, partnerName);
    if (!model) return false;
    setPending({ model, resend });
    return true;
  }, [isAdmin]);
  // resend(ov): the caller merges ov (credit_override + reason + new idempotency key) into the SAME request. The notice shows the Override button
  // for every refusal that has a resend (greyed "Admin only" for a non-admin); only the drawer's confirm calls it.
  const proceed = pending && pending.resend ? (ov) => { const r = pending.resend; setPending(null); r(ov); } : null;
  const notice = pending ? <CreditLimitNotice model={pending.model} onProceed={proceed} onDismiss={clear} /> : null;
  return { catchCredit, clear, notice };
}
